// Shop pricing — ONE implementation shared by the React cart, the build-time
// static pages and (mirrored line-for-line) backend/shop_pricing.py. The backend
// recomputes every total from the published catalogue and never trusts the
// browser; if you change a rule here, change it there and re-run the parity
// test (backend/tests/test_shop.py reads fixtures generated from this file).
//
// All arithmetic is in integer CENTS. Rounding is half-up on non-negative values.
const { installTiers, installAddons, shipping, installAreas, findVariant, supplierCodeFor } = require('../content/products');
const { serviceAreas } = require('../content/serviceAreas');

const GST_RATE = 0.15;

function roundHalfUp(x) {
  return Math.floor(x + 0.5);
}
function toCents(dollars) {
  return roundHalfUp(dollars * 100);
}
function exFromInc(cents) {
  return roundHalfUp(cents / (1 + GST_RATE));
}
function incFromEx(cents) {
  return roundHalfUp(cents * (1 + GST_RATE));
}
function gstPortion(incCents) {
  return incCents - exFromInc(incCents);
}
function fmt(cents) {
  const dollars = cents / 100;
  return '$' + dollars.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtWhole(cents) {
  return '$' + Math.round(cents / 100).toLocaleString('en-NZ');
}

function tierIncCents(tierKey) {
  const t = installTiers[tierKey];
  if (!t) return 0;
  return incFromEx(toCents(t.exGst));
}
// Is this suburb in the install service area? The North Shore list plus installAreas. The cart
// no longer asks it (the customer ticks "inside the Auckland service area" instead, Hugh 22 Sep
// 2026); this stays for the service-area list and for anything that still carries a suburb.
function inServiceArea(name) {
  if (!name) return false;
  const n = String(name).trim().toLowerCase();
  if (serviceAreas.some((a) => a.name.toLowerCase() === n || a.slug === n)) return true;
  return installAreas.central.concat(installAreas.outer).some((s) => s.toLowerCase() === n);
}

// The install tier is the lock's own (Hugh, 22 Sep 2026): one fitted price per lock, whatever
// the door. The door answers that used to move it (timber / aluminium, what was on the door)
// are still taken — the chooser needs them to find the lock — but they never touch the price.
function resolveTier(product) {
  return (product && product.install && product.install.tier) || 'standard';
}

// Courier per parcel, priced so freight never costs Shore Doors (Hugh, 20 Sep 2026): every
// supplier ships its own parcel at its own rate (shipping.perSupplier); a supplier that ships
// free above a subtotal (Elements, $100 ex GST) makes that parcel $0; the order never pays
// less than the base rate (shipping.standard / rural). lines = [{ supplier, lineCents }] inc
// GST. Returns { cents, parcels }. Mirrored by backend/shop_pricing.py courier_for_lines.
function courierForLines(lines, choice) {
  const k = choice === 'rural' ? 'rural' : 'standard';
  const bySupplier = {};
  for (const l of lines || []) {
    const s = l.supplier || 'unknown';
    bySupplier[s] = (bySupplier[s] || 0) + (l.lineCents || 0);
  }
  const names = Object.keys(bySupplier);
  let cents = 0;
  for (const s of names) {
    const rate = (shipping.perSupplier || {})[s] || {};
    // freeOverEx is ex-GST dollars; the subtotal is inc-GST cents: × 115 puts them on one scale.
    const thresholdInc = rate.freeOverEx ? Math.round(rate.freeOverEx * 115) : null;
    if (thresholdInc !== null && bySupplier[s] >= thresholdInc) continue;
    cents += toCents(rate[k] != null ? rate[k] : shipping[k]);
  }
  return { cents: Math.max(cents, toCents(shipping[k])), parcels: Math.max(names.length, 1) };
}
// The same for the cart page's { variantId, qty } lines, and for one product (the feeds and JSON-LD).
function courierForCart(cartItems, choice) {
  const lines = [];
  for (const line of cartItems || []) {
    const hit = findVariant(line.variantId);
    if (!hit) continue;
    const qty = Math.max(1, Math.min(10, parseInt(line.qty, 10) || 1));
    lines.push({ supplier: hit.variant.supplier, lineCents: toCents(hit.product.priceIncGst) * qty });
  }
  return courierForLines(lines, choice);
}
function courierForProductCents(product, choice) {
  const v = product.variants && product.variants[0];
  return courierForLines([{ supplier: v ? v.supplier : 'unknown', lineCents: toCents(product.priceIncGst) }], choice).cents;
}

// cart = { items: [{ variantId, qty, backsetMm, install, fromChooser, door }],
//   install: { inArea, hubSetup, preferredTimes, doorNotes } | null, shippingChoice: 'standard' | 'rural' }
// Supply & install is per line (Hugh, 22 Sep 2026): each lock line carries its own `install`
// flag and is priced at its own lock's tier; `cart.install` holds what the visit shares — the
// service-area tick, the notes, the hub set-up. There is no travel line and no suburb: the one
// fitted price covers the whole Auckland service area, and the price does not change unless
// it is a different lock entirely. A cart whose lines carry no `install` flag at all is the
// old blanket shape: `install.selected` then fits every eligible line (older bundles and
// stored carts). With any line fitted nothing ships — the rest of the order comes with the
// installer. Returns a fully itemised quote in cents, or { error } for an invalid cart.
function quoteCart(cart) {
  const shared = cart.install || {};
  const lines = cart.items || [];
  const perLine = lines.some((l) => l.install !== undefined && l.install !== null);
  const blanket = !perLine && !!shared.selected;
  const items = [];
  let itemsCents = 0;
  let installCount = 0;
  let installCents = 0;
  const tiers = [];
  for (const line of lines) {
    const hit = findVariant(line.variantId);
    if (!hit) return { error: `Unknown item ${line.variantId}` };
    const qty = Math.max(1, Math.min(10, parseInt(line.qty, 10) || 1));
    const noMortice = !!(line.noMortice && hit.product.mortice);
    const unit = toCents(hit.product.priceIncGst) + (noMortice ? toCents(hit.product.mortice.leversetOnlyDeltaIncGst) : 0);
    const saleStatus = hit.product.saleStatus || 'sale';
    if (saleStatus === 'quote') return { error: `${hit.product.shortName} is quoted per door — request a quote instead of buying online` };
    if (saleStatus === 'coming-soon') return { error: `${hit.product.shortName} is coming soon and can't be ordered yet` };
    // The backset is chosen in the cart, and never on the Find-my-lock path (Hugh, 22 Sep
    // 2026): a line without one goes through with backsetMm null and a '?' in the supplier
    // code, and backsetPending flags the order as needing the door photos before it is
    // ordered in. 'unsure' is the cart's "Not sure" answer, the same thing. A size the line
    // does carry must be one the lock comes in.
    const backsets = hit.product.fit.backsetsMm || [];
    const sized = !noMortice && backsets.length > 0;
    const asked = line.backsetMm === 'unsure' ? null : line.backsetMm;
    const backsetMm = sized && asked != null && backsets.includes(Number(asked)) ? Number(asked) : null;
    if (sized && asked != null && backsetMm === null) {
      return { error: `${hit.product.shortName} does not come in ${asked} mm` };
    }
    const eligible = !!(hit.product.install && hit.product.install.eligible);
    const fitted = eligible && (perLine ? line.install === true : blanket);
    let lineInstall = null;
    if (fitted) {
      const tier = resolveTier(hit.product);
      const unitCents = tierIncCents(tier);
      lineInstall = { tier, tierLabel: installTiers[tier].label, unitCents, cents: unitCents * qty };
      let group = tiers.find((t) => t.tier === tier);
      if (!group) {
        group = { tier, tierLabel: installTiers[tier].label, count: 0, unitCents, cents: 0 };
        tiers.push(group);
      }
      group.count += qty;
      group.cents += unitCents * qty;
      installCount += qty;
      installCents += unitCents * qty;
    }
    items.push({
      variantId: hit.variant.id,
      productSlug: hit.product.slug,
      name: hit.product.name,
      shortName: hit.product.shortName,
      variantLabel: hit.variant.label,
      backsetMm,
      backsetPending: sized && backsetMm === null,
      noMortice,
      backsetMeasuredMm: line.backsetMeasuredMm ? Number(line.backsetMeasuredMm) : null,
      supplier: hit.variant.supplier,
      supplierSku: supplierCodeFor(hit.product, hit.variant, backsetMm, noMortice),
      qty,
      unitCents: unit,
      lineCents: unit * qty,
      installEligible: eligible,
      install: lineInstall,
      fromChooser: !!line.fromChooser,
      door: line.door ? String(line.door).slice(0, 120) : null,
      saleStatus,
    });
    itemsCents += unit * qty;
  }
  if (items.length === 0) return { error: 'Your cart is empty' };

  const install = installCount > 0 ? shared : null;
  const installOnly = items.find((i) => i.saleStatus === 'install-only' && !i.install);
  if (installOnly) {
    return { error: `${installOnly.shortName} is supply & install only (Auckland) — add installation or remove it` };
  }
  let hubSetupCents = 0;
  if (install && install.hubSetup) hubSetupCents = incFromEx(toCents(installAddons.hubSetup.exGst));

  let shippingCents = 0;
  let shippingLabel = 'No shipping — fitted by Shore Doors';
  let parcels = 0;
  if (!install) {
    const choice = cart.shippingChoice === 'rural' ? 'rural' : 'standard';
    const threshold = shipping.freeOver ? toCents(shipping.freeOver) : null;
    if (threshold !== null && itemsCents >= threshold) {
      shippingCents = 0;
      shippingLabel = `Free NZ-wide courier (orders over ${fmtWhole(threshold)})`;
    } else {
      const c = courierForLines(items, choice);
      shippingCents = c.cents;
      parcels = c.parcels;
      shippingLabel = (choice === 'rural' ? 'Rural courier' : 'Courier') + (c.parcels > 1 ? ` · ${c.parcels} parcels` : '');
    }
  }

  const totalCents = itemsCents + installCents + hubSetupCents + shippingCents;
  // `tiers` is one row per price on the order ("Digital lock install × 2", "Deadbolt / latch
  // install × 1"); tier / tierLabel / unitCents describe the first row for anything that still
  // reads the one-tier shape, and tierLabel names every tier when there is more than one.
  // `inArea` is the customer's tick that the door is inside the Auckland service area.
  return {
    items,
    itemsCents,
    install: install
      ? {
          tier: tiers[0].tier,
          tierLabel: tiers.map((t) => t.tierLabel).join(' + '),
          count: installCount,
          unitCents: tiers[0].unitCents,
          cents: installCents,
          tiers,
          hubSetupCents,
          inArea: !!install.inArea,
          suburb: install.suburb || null,
        }
      : null,
    shippingCents,
    shippingLabel,
    parcels,
    shippingChoice: install ? null : cart.shippingChoice === 'rural' ? 'rural' : 'standard',
    totalCents,
    gstCents: gstPortion(totalCents),
    exGstCents: exFromInc(totalCents),
  };
}

// Fitted price shown first on product pages: product + its default tier, one unit.
function fittedFromCents(product) {
  if (!product.install || !product.install.eligible) return null;
  return toCents(product.priceIncGst) + tierIncCents(product.install.tier || 'standard');
}

module.exports = {
  GST_RATE,
  roundHalfUp,
  toCents,
  exFromInc,
  incFromEx,
  gstPortion,
  fmt,
  fmtWhole,
  tierIncCents,
  inServiceArea,
  resolveTier,
  quoteCart,
  fittedFromCents, courierForLines, courierForCart, courierForProductCents };
